import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import NavBar from "../../components/navbar/NavBar";
import Swal from "sweetalert2";
import { ProfileContext } from "../../components/context/ProfileContext";
import { useSelector } from "react-redux";
import Avatar from "@mui/material/Avatar";
import {
  validateAddress,
  validateFirstName,
  validateLastName,
  validateNoNumbersOrSpecialChars,
  validateNoNumbersOrSpecialCharsInCity,
} from "../../utils/validation";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO, isValid } from "date-fns";
import Tooltip from "@mui/material/Tooltip";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useLocalWallet from "../../utils/hooks";
import ChangeWalletPopup from "./ChangeWalletPopup";
import messages from "../../constants/messages";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { faClock, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const Profile = () => {
  const {
    setUserProfileImage,
    setAdminProfileImage,
    setUserFirstname,
    setUserLastname,
    setAdminFirstname,
    setAdminLastname,
  } = useContext(ProfileContext);
  const { copyToClipboard } = useLocalWallet();
  const [firstname, setFirstNameLocal] = useState("");
  const [lastname, setLastNameLocal] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDOB] = useState(null);
  const [description, setDescription] = useState("");
  const [role, setRole] = useState("");
  const [profileImage, setProfileImageLocal] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [errorFirstName, setErrorFirstName] = useState("");
  const [errorLastName, setErrorLastName] = useState("");
  const [errorPhone, setErrorPhone] = useState("");
  const [errorAddress, setErrorAddress] = useState("");
  const [errorCity, setErrorCity] = useState("");
  const [errorState, setErrorState] = useState("");
  const [errorCountry, setErrorCountry] = useState("");
  const [errorZip, setErrorZip] = useState("");
  const [errorDescription, setErrorDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsopen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [isPhoneUsed, setIsPhoneUsed] = useState(false);
  const [wallet, setWallet] = useState("");
  const [userReason, setUserReason] = useState("");
  const [icon, setIcon] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  const user = useSelector((state) => state.userStore.user);
  const token = user.auth;
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (token) {
      const role = user.role;
      setRole(role);

      const endpoint =
        role === "admin" ? "/admin/getAdminProfile" : "/users/getUserProfile";

      axios
        .get(`${process.env.REACT_APP_API_ENDPOINT_URL}${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data.status === 200) {
            const userData = response.data.data;
            setFirstNameLocal(capitalizeFirstLetter(userData.firstName || ""));
            setLastNameLocal(capitalizeFirstLetter(userData.lastName || ""));
            setEmail(userData.email || "");
            setAddress(userData.address || "");
            setCity(userData.city || "");
            setState(userData.state || "");
            setZip(userData.zip || "");
            setPhone(userData.phone || "");
            setCountry(userData.country || "");

            const dobDate = parseISO(userData.dob ?? "");
            if (isValid(dobDate)) {
              setDOB(dobDate);
            }
            setDescription(userData.description || "");
            setWalletAddress(userData.walletAddress || "");

            if (
              userData.profileImage &&
              typeof userData.profileImage === "string" &&
              userData.profileImage.trim() !== ""
            ) {
              try {
                const decodedData = Buffer.from(
                  userData.profileImage.replace(/^data:.*,/, ""),
                  "base64"
                );

                const uint8Array = new Uint8Array(decodedData.length);
                for (let i = 0; i < decodedData.length; i++) {
                  uint8Array[i] = decodedData[i];
                }

                const blob = new Blob([uint8Array]);
                const imgSrc = URL.createObjectURL(blob);
                setImageBase64(imgSrc);
              } catch (err) {
                Swal.fire({
                  title: messages.titles.error,
                  text: messages.errors.decodeImageFailed,
                  icon: "error",
                  confirmButtonText: messages.buttons.ok,
                });
              }
            }
          } else {
            Swal.fire({
              title: messages.titles.error,
              text: `${response.data.message}`,
              icon: "error",
              confirmButtonText: messages.buttons.ok,
            });
          }
        })
        .catch((err) => {
          console.error("API call error:", err);
          Swal.fire({
            title: messages.titles.error,
            text: messages.errors.somethingWentWrong,
            icon: "error",
            confirmButtonText: messages.buttons.ok,
          });
        });
    }
  }, [token, user.role]);

  const handleChange = async (value) => {
    setErrorPhone("");
    const formattedValue = value.startsWith("+") ? value : `+${value}`;
    setPhone(formattedValue);
    const isValidPhone = validatePhoneNumber(formattedValue);
    setIsValidPhone(isValidPhone);
    if (!isValidPhone) {
      setErrorPhone(messages.errors.invalidPhone);
    } else {
      setErrorPhone("");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_ENDPOINT_URL}/users/isPhoneNumberUsed/${formattedValue}`,
          { headers: { "Content-Type": "application/json" } }
        );
        const phoneStatus = response?.data?.phoneStatus;
        setIsPhoneUsed(phoneStatus);
        if (phoneStatus) {
          setErrorPhone(messages.errors.phoneExists);
        } else {
          setErrorPhone("");
        }
      } catch (error) {
        setErrorPhone(messages.errors.phoneValidationFailed);
      }
    }
  };

  const validatePhoneNumber = (phone) => {
    const parsedNumber = parsePhoneNumberFromString(phone);
    return parsedNumber ? parsedNumber.isValid() : false;
  };

  const handleUpdateProfile = (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("firstname", firstname ? firstname : "");
    formData.append("lastname", lastname ? lastname : "");
    formData.append("address", address ? address : "");
    formData.append("city", city ? city : "");
    formData.append("state", state ? state : "");
    formData.append("zip", zip ? zip : "");
    formData.append("phone", phone ? phone : "");
    formData.append(
      "dob",
      dob && isValid(dob) ? format(dob, "yyyy-MM-dd") : ""
    );
    formData.append("country", country ? country : "");
    formData.append("description", description ? description : "");
    formData.append("walletAddress", walletAddress ? walletAddress : "");

    if (profileImage) {
      formData.append("image", profileImage);
    }

    if (firstname) {
      if (!validateFirstName(firstname)) {
        setErrorFirstName(
          firstname.length > 0 && firstname.length < 3
            ? messages.errors.invalidFirstName
            : messages.errors.firstNameRequired
        );
        setLoading(false);
        return;
      }
    }

    if (lastname) {
      if (!validateLastName(lastname)) {
        setErrorLastName(
          lastname.length > 0 && lastname.length < 3
            ? messages.errors.invalidLastName
            : messages.errors.lastNameRequired
        );
        setLoading(false);
        return;
      }
    }

    if (!phone) {
      setErrorPhone(messages.errors.phoneRequired);
      setLoading(false);
      return;
    }

    if (!isValidPhone) {
      setErrorPhone(messages.errors.invalidPhone);
      setLoading(false);
      return;
    }

    if (isPhoneUsed) {
      setErrorPhone(messages.errors.phoneExists);
      setLoading(false);
      return;
    }

    if (address) {
      if (!validateAddress(address)) {
        setErrorAddress(messages.errors.invalidAddress);
        setLoading(false);
        return;
      }
    }

    const endpoint =
      role === "admin"
        ? "/admin/updateAdminProfile"
        : "/users/updateUserProfile";

    axios
      .post(`${process.env.REACT_APP_API_ENDPOINT_URL}${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.status === 200) {
          Swal.fire({
            title: messages.titles.success,
            text: messages.success.successUpdateProfile,
            icon: "success",
            confirmButtonText: messages.buttons.ok,
          });
          if (role === "admin") {
            setAdminProfileImage(imageBase64);
            setAdminFirstname(firstname);
            setAdminLastname(lastname);
          } else {
            setUserProfileImage(imageBase64);
            setUserFirstname(firstname);
            setUserLastname(lastname);
          }
        } else {
          Swal.fire({
            title: messages.titles.error,
            text: `${response.data.message}`,
            icon: "error",
            confirmButtonText: messages.buttons.ok,
          });
        }
      })
      .catch(() => {
        Swal.fire({
          title: messages.titles.error,
          text: messages.errors.somethingWentWrong,
          icon: "error",
          confirmButtonText: messages.buttons.ok,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setProfileImageLocal(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageBase64(reader.result);
      if (role === "admin") {
        setAdminProfileImage(reader.result);
      } else {
        setUserProfileImage(reader.result);
      }
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const stringAvatar = () => {
    const firstNameInitial = firstname
      ? firstname.split(" ")[0][0]?.toUpperCase()
      : "";
    const lastNameInitial = lastname
      ? lastname.split(" ")[0][0]?.toUpperCase()
      : "";
    return {
      children: `${firstNameInitial}${lastNameInitial}`,
    };
  };

  const imageButtonText =
    profileImage || imageBase64 ? "Edit Profile Image" : "Upload Profile Image";

  const handlePopupClose = () => setShowPopup(false);
  const handlePopupShow = () => setShowPopup(true);

  const handlePopupSubmit = (newWalletAddress, reason) => {
    const responseData = {
      walletAddress: newWalletAddress,
      reason: reason,
    };

    setLoading(true);

    axios
      .post(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/users/updateWalletAddress`,
        responseData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => {
        if (response.data.status === 200) {
          Swal.fire({
            title: messages.titles.success,
            text: messages.success.successRequest,
            icon: "success",
            confirmButtonText: messages.buttons.ok,
          });
          fetchWalletChangeHistory();
        } else if (response.data.status === 208) {
          Swal.fire({
            title: messages.titles.warning,
            text: messages.errors.requestPending,
            icon: "warning",
            confirmButtonText: messages.buttons.ok,
          });
        } else if (
          response.data.status === 400 &&
          response.data.message === messages.errors.walletAlreadyUsed
        ) {
          Swal.fire({
            title: messages.titles.error,
            text: messages.errors.walletAlreadyUsed,
            icon: "error",
            confirmButtonText: messages.buttons.ok,
          });
        } else if (
          response.data.status === 400 &&
          response.data.message === messages.errors.alreadyPendingInProfile
        ) {
          Swal.fire({
            title: messages.titles.error,
            text: messages.errors.alreadyPendingInProfile,
            icon: "warning",
            confirmButtonText: messages.buttons.ok,
          });
        } else if (
          response.data.status === 400 &&
          response.data.message === messages.errors.walletAlreadyUsedBySomeOne
        ) {
          Swal.fire({
            title: messages.titles.error,
            text: messages.errors.walletAlreadyUsedBySomeOne,
            icon: "error",
            confirmButtonText: messages.buttons.ok,
          });
        } else if (
          response.data.status === 400 &&
          response.data.message === messages.errors.provideWallet
        ) {
          Swal.fire({
            title: messages.titles.error,
            text: messages.errors.provideWallet,
            icon: "error",
            confirmButtonText: messages.buttons.ok,
          });
        } else {
          Swal.fire({
            title: messages.titles.error,
            text: messages.errors.somethingWentWrong,
            icon: "error",
            confirmButtonText: messages.buttons.ok,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        Swal.fire({
          title: messages.titles.error,
          text: messages.errors.somethingWentWrong,
          icon: "error",
          confirmButtonText: messages.buttons.ok,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchWalletChangeHistory = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/users/getLastPendingWalletAddress`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (
        response.status === 200 &&
        response.data?.status === 200 &&
        response.data?.walletAddress
      ) {
        const walletAddress = response.data?.walletAddress;
        setWallet(walletAddress?.walletAddress);
        setUserReason(walletAddress?.reason);

        switch (walletAddress.status) {
          case 0:
            setIcon(
              <FontAwesomeIcon icon={faClock} style={{ color: "orange" }} />
            );
            setStatusMessage(" is currently pending");
            break;
          case 1:
            setIcon(
              <FontAwesomeIcon icon={faCheck} style={{ color: "green" }} />
            );
            setStatusMessage(" has been approved");
            break;
          case 2:
            setIcon(
              <FontAwesomeIcon icon={faTimes} style={{ color: "red" }} />
            );
            setStatusMessage(" has been rejected");
            break;
          default:
            setIcon(null);
            setStatusMessage(" has an unknown status");
            break;
        }
      } else if (response.data.status === 404) {
        setIcon(null);
        setStatusMessage(
          "You don't have any active wallet address change requests"
        );
      } else {
        throw new Error("Unexpected response");
      }
    } catch (error) {
      console.error("Error fetching wallet change history:", error);
      if (error.response && error.response.data.status === 404) {
        setIcon(null);
        setStatusMessage(
          "You don't have any active wallet address change requests"
        );
      } else {
        Swal.fire({
          title: messages.titles.error,
          text: messages.errors.somethingWentWrong,
          icon: "error",
          confirmButtonText: messages.buttons.ok,
        });
      }
    }
  };

  const handleDescriptionChange = (e) => {
    const inputValue = e.target.value;
    if (validateDescription(inputValue)) {
      const capitalizedValue = capitalizeFirstLetter(inputValue);
      setDescription(capitalizedValue);
    }
  };

  const validateDescription = (input) => {
    const leadingWhitespaceRegex = /^\s/;
    if (leadingWhitespaceRegex.test(input)) {
      setErrorDescription("Description should not start with whitespace.");
      return false;
    }
    if (input.length > 3000) {
      setErrorDescription("Description is too long.");
      return false;
    }
    setErrorDescription("");
    return true;
  };

  return (
    <>
      <NavBar />
      <div className={`sidebar-sec ${isOpen === true ? "active" : ""}`}>
        <section id="content-wrapper">
          <h4 className="my-dash my-profile-heading">My Profile</h4>
          <div className="container-dashboard-right">
            <div className="page-head-name">
              <p className="content-title">Profile</p>
            </div>
            <div className="container-fluid">
              <form onSubmit={handleUpdateProfile}>
                <div className="profile-main-wrapper">
                  <div className="profile-form-wrapper">
                    <div className="row">
                      <div className="col-lg-3 col-sm-12">
                        <div className="profile_image-and-detail">
                          <div className="profile-img-header">
                            <div className="avatar-upload">
                              <div className="avatar-edit">
                                <input
                                  type="file"
                                  id="imageUpload"
                                  accept="image/*"
                                  onChange={(e) => handleFileChange(e)}
                                  className="profile-image-set"
                                />
                              </div>
                              <div className="avatar-preview">
                                {imageBase64 && (
                                  <img
                                    src={imageBase64}
                                    alt="User"
                                    className="profile-image-preview"
                                  />
                                )}
                                {!imageBase64 && (
                                  <Avatar alt="User" {...stringAvatar()} />
                                )}
                              </div>
                            </div>
                            <div className="profile-btn">
                              <button
                                type="button"
                                id="uploadButton"
                                className="btn btn-light"
                                onClick={() =>
                                  document.getElementById("imageUpload").click()
                                }
                              >
                                {imageButtonText}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-9 col-sm-12">
                        <div className="row">
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                First Name
                              </label>
                              <input
                                type="text"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter firstname"
                                value={firstname || ""}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (
                                    validateNoNumbersOrSpecialChars(inputValue)
                                  ) {
                                    const capitalizedValue =
                                      capitalizeFirstLetter(inputValue);
                                    setFirstNameLocal(capitalizedValue);
                                    setErrorFirstName("");
                                  } else {
                                    setErrorFirstName(
                                      "Only alphabet characters are allowed."
                                    );
                                  }
                                }}
                                maxLength={32}
                              />
                            </div>
                            {errorFirstName && (
                              <p className="text-warning">{errorFirstName}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Last Name
                              </label>
                              <input
                                type="text"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter lastname"
                                value={lastname || ""}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (
                                    validateNoNumbersOrSpecialChars(inputValue)
                                  ) {
                                    const capitalizedValue =
                                      capitalizeFirstLetter(inputValue);
                                    setLastNameLocal(capitalizedValue);
                                    setErrorLastName("");
                                  } else {
                                    setErrorLastName(
                                      "Only alphabet characters are allowed."
                                    );
                                  }
                                }}
                                maxLength={32}
                              />
                            </div>
                            {errorLastName && (
                              <p className="text-warning">{errorLastName}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Email<span>*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter email"
                                value={email || ""}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-box">
                              <label htmlFor="" className="form-label">
                                Phone<span>*</span>
                              </label>
                              <PhoneInput
                                country={"us"}
                                value={phone}
                                className="form-control"
                                onChange={(value, country) =>
                                  handleChange(value, country)
                                }
                                inputProps={{
                                  required: true,
                                  placeholder: "Enter phone number",
                                }}
                              />
                            </div>
                            {errorPhone && (
                              <p className="text-warning">{errorPhone}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label
                                htmlFor=""
                                className="form-label dob-label"
                              >
                                DOB
                              </label>
                              <DatePicker
                                selected={dob}
                                onChange={(date) => setDOB(date)}
                                dateFormat="MM-dd-yyyy"
                                maxDate={new Date()}
                                className="form-control Profile-input mt-1 dob"
                                placeholderText="MM-DD-YYYY"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Solana Wallet Address
                              </label>
                              <div className="copy-wallet-profile">
                                <input
                                  type="text"
                                  className="form-control Profile-input mt-1"
                                  placeholder="Enter Wallet Address"
                                  value={walletAddress || ""}
                                  onChange={(e) =>
                                    setWalletAddress(e.target.value)
                                  }
                                  maxLength={100}
                                  disabled={user?.role === "user"}
                                />
                                {walletAddress && (
                                  <Tooltip title="Copy Wallet Address" arrow>
                                    <FontAwesomeIcon
                                      icon={faCopy}
                                      className="copy-wallet-icon copy-size"
                                      onClick={() =>
                                        copyToClipboard(walletAddress)
                                      }
                                    />
                                  </Tooltip>
                                )}
                              </div>
                              {!isAdmin && (
                                <div className="change-wallet">
                                  <a href="#" onClick={handlePopupShow}>
                                    Change Wallet?
                                  </a>
                                </div>
                              )}
                            </div>
                            <ChangeWalletPopup
                              show={showPopup}
                              handleClose={handlePopupClose}
                              handleSubmit={handlePopupSubmit}
                              fetchWalletChangeHistory={
                                fetchWalletChangeHistory
                              }
                              wallet={wallet}
                              userReason={userReason}
                              icon={icon}
                              statusMessage={statusMessage}
                            />
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Address
                              </label>
                              <input
                                type="text"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter address"
                                value={address || ""}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (!inputValue.startsWith(" ")) {
                                    const capitalizedValue =
                                      capitalizeFirstLetter(inputValue);
                                    setAddress(capitalizedValue);
                                    setErrorAddress("");
                                  } else {
                                    setErrorAddress(
                                      "Address cannot start with a space."
                                    );
                                  }
                                }}
                                maxLength={200}
                              />
                            </div>
                            {errorAddress && (
                              <p className="text-warning">{errorAddress}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                City
                              </label>
                              <input
                                type="text"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter City"
                                value={city || ""}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (
                                    validateNoNumbersOrSpecialCharsInCity(
                                      inputValue
                                    )
                                  ) {
                                    const capitalizedValue =
                                      capitalizeFirstLetter(inputValue);
                                    setCity(capitalizedValue);
                                    setErrorCity("");
                                  } else {
                                    setErrorCity(
                                      "Only alphabet characters are allowed."
                                    );
                                  }
                                }}
                                maxLength={32}
                              />
                            </div>
                            {errorCity && (
                              <p className="text-warning">{errorCity}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                State
                              </label>
                              <input
                                type="text"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter state"
                                value={state || ""}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (
                                    validateNoNumbersOrSpecialCharsInCity(
                                      inputValue
                                    )
                                  ) {
                                    const capitalizedValue =
                                      capitalizeFirstLetter(inputValue);
                                    setState(capitalizedValue);
                                    setErrorState("");
                                  } else {
                                    setErrorState(
                                      "Only alphabet characters are allowed."
                                    );
                                  }
                                }}
                                maxLength={32}
                              />
                            </div>
                            {errorState && (
                              <p className="text-warning">{errorState}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Country
                              </label>
                              <input
                                type="text"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter country"
                                value={country || ""}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (
                                    validateNoNumbersOrSpecialCharsInCity(
                                      inputValue
                                    )
                                  ) {
                                    const capitalizedValue =
                                      capitalizeFirstLetter(inputValue);
                                    setCountry(capitalizedValue);
                                    setErrorCountry("");
                                  } else {
                                    setErrorCountry(
                                      "Only alphabet characters are allowed."
                                    );
                                  }
                                }}
                                maxLength={32}
                              />
                            </div>
                            {errorCountry && (
                              <p className="text-warning">{errorCountry}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                ZIP Code
                              </label>
                              <input
                                type="number"
                                className="form-control Profile-input mt-1"
                                placeholder="Enter zip"
                                value={zip || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 10) {
                                    setZip(value);
                                    setErrorZip("");
                                    if (value.length > 10) {
                                      setErrorZip(
                                        "Please enter a valid Zip code"
                                      );
                                    }
                                  }
                                }}
                                maxLength={10}
                              />
                            </div>
                            {errorZip && (
                              <p className="text-warning">{errorZip}</p>
                            )}
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Description/Bio
                              </label>
                              <textarea
                                className="form-control mb-3"
                                id=""
                                rows="3"
                                value={description || ""}
                                onChange={handleDescriptionChange}
                                maxLength={3000}
                              ></textarea>
                            </div>
                            {errorDescription && (
                              <p className="text-warning">{errorDescription}</p>
                            )}
                          </div>
                          <div className="profile-btn">
                            <button
                              type="submit"
                              className="btn"
                              disabled={loading}
                            >
                              {loading ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              ) : (
                                "Update Profile"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Profile;
